import { Component, lazy, Suspense, type ComponentType, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

import RouteScrollToTop from "./components/RouteScrollToTop.tsx";
import PageSkeleton from "./components/PageSkeleton.tsx";


type LazyRouteModule<P = unknown> = { default: ComponentType<P> };

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const getFailedImportUrl = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  const match = message.match(/(?:https?:\/\/[^\s"')]+|\/src\/[^\s"')]+|\/assets\/[^\s"')]+)/);
  return match?.[0] ?? null;
};

const importFreshModule = (url: string) => {
  const freshUrl = new URL(url, window.location.origin);
  freshUrl.searchParams.set("t", String(Date.now()));
  return import(/* @vite-ignore */ freshUrl.href) as Promise<LazyRouteModule<unknown>>;
};

const lazyRoute = <P,>(factory: () => Promise<LazyRouteModule<P>>) =>
  lazy(async () => {
    let lastError: unknown;

    for (const delay of [0, 250, 750]) {
      if (delay) await wait(delay);
      try {
        return await factory();
      } catch (error) {
        lastError = error;
      }
    }

    if (typeof window !== "undefined") {
      const failedUrl = getFailedImportUrl(lastError);
      if (failedUrl) {
        return importFreshModule(failedUrl) as Promise<LazyRouteModule<P>>;
      }
    }

    throw lastError;
  }) as ComponentType<P>;

class RouteErrorBoundary extends Component<
  { children: ReactNode; resetKey: string },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: { resetKey: string }) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
          <div className="max-w-md space-y-4 text-center">
            <p className="font-heading text-lg font-bold text-primary">This page had trouble loading.</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded border border-primary/40 bg-primary px-4 py-2 font-heading text-xs font-bold uppercase tracking-wider text-primary-foreground transition-opacity hover:opacity-90"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const Index = lazyRoute(() => import("./pages/Index.tsx"));
const About = lazyRoute(() => import("./pages/About.tsx"));
const Memberships = lazyRoute(() => import("./pages/Memberships.tsx"));
const Access = lazyRoute(() => import("./pages/Access.tsx"));
const AccessCoaching = lazyRoute(() => import("./pages/AccessCoaching.tsx"));
const AccessSupport = lazyRoute(() => import("./pages/AccessSupport.tsx"));
const SquadUp = lazyRoute(() => import("./pages/SquadUp.tsx"));
const StartHub = lazyRoute(() => import("./pages/StartHub.tsx"));
const Featured = lazyRoute(() => import("./pages/Featured.tsx"));
const Mission = lazyRoute(() => import("./pages/Mission.tsx"));
const TacticalLoadouts = lazyRoute(() => import("./pages/TacticalLoadouts.tsx"));
const FreeTraining = lazyRoute(() => import("./pages/FreeTraining.tsx"));
const DiscordHelp = lazyRoute(() => import("./pages/DiscordHelp.tsx"));
const DiscordSetup = lazyRoute(() => import("./pages/DiscordSetup.tsx"));
const DiscordEmbed = lazyRoute(() => import("./pages/DiscordEmbed.tsx"));
const JoinDiscord = lazyRoute(() => import("./pages/JoinDiscord.tsx"));
const Contact = lazyRoute(() => import("./pages/Contact.tsx"));
const Blog = lazyRoute(() => import("./pages/Blog.tsx"));
const BlogArticle = lazyRoute(() => import("./pages/BlogArticle.tsx"));
const GamePage = lazyRoute(() => import("./pages/GamePage.tsx"));
const Games = lazyRoute(() => import("./pages/Games.tsx"));
const RanksAccolades = lazyRoute(() => import("./pages/RanksAccolades.tsx"));
const NotFound = lazyRoute(() => import("./pages/NotFound.tsx"));
const PrivacyPolicy = lazyRoute(() => import("./pages/PrivacyPolicy.tsx"));
const Accolades = lazyRoute(() => import("./pages/Accolades.tsx"));
const ClanDirectory = lazyRoute(() => import("./pages/ClanDirectory.tsx"));
const FindAUnit = lazyRoute(() => import("./pages/FindAUnit.tsx"));
const ClanPage = lazyRoute(() => import("./pages/ClanPage.tsx"));
const MemberProfilePage = lazyRoute(() => import("./pages/MemberProfilePage.tsx"));
const ResetPassword = lazyRoute(() => import("./pages/ResetPassword.tsx"));
const SubmitLog = lazyRoute(() => import("./pages/SubmitLog.tsx"));
const OfficerTools = lazyRoute(() => import("./pages/OfficerTools.tsx"));
const MyProfile = lazyRoute(() => import("./pages/MyProfile.tsx"));
const MySquad = lazyRoute(() => import("./pages/MySquad.tsx"));
const Events = lazyRoute(() => import("./pages/Events.tsx"));
const SquadFinder = lazyRoute(() => import("./pages/SquadFinder.tsx"));
const WeeklyRecap = lazyRoute(() => import("./pages/WeeklyRecap.tsx"));
const Playbook = lazyRoute(() => import("./pages/Playbook.tsx"));
const Summary = lazyRoute(() => import("./pages/Summary.tsx"));
const CommandGroups = lazyRoute(() => import("./pages/CommandGroups.tsx"));
const OperatorProfile = lazyRoute(() => import("./pages/OperatorProfile.tsx"));
const ShareOperatorProfile = lazyRoute(() => import("./pages/ShareOperatorProfile.tsx"));
const ShareMemberProfile = lazyRoute(() => import("./pages/ShareMemberProfile.tsx"));
const Help = lazyRoute(() => import("./pages/Help.tsx"));
const FAQ = lazyRoute(() => import("./pages/FAQ.tsx"));
const Prizes = lazyRoute(() => import("./pages/Prizes.tsx"));
const ClanWars = lazyRoute(() => import("./pages/ClanWars.tsx"));
const HowItWorks = lazyRoute(() => import("./pages/HowItWorks.tsx"));
const FindTeammates = lazyRoute(() => import("./pages/FindTeammates.tsx"));
const SocialOps = lazyRoute(() => import("./pages/SocialOps.tsx"));
const OperatorPath = lazyRoute(() => import("./pages/OperatorPath.tsx"));
const WarTrack = lazyRoute(() => import("./pages/WarTrack.tsx"));
const WarLobby = lazyRoute(() => import("./pages/WarLobby.tsx"));
const RivalryPage = lazyRoute(() => import("./pages/RivalryPage.tsx"));
const Spotlight = lazyRoute(() => import("./pages/Spotlight.tsx"));
const SpotlightWyldeJezter = lazyRoute(() => import("./pages/SpotlightWyldeJezter.tsx"));
const BringYourCrew = lazyRoute(() => import("./pages/BringYourCrew.tsx"));
const ArchetypesIndex = lazyRoute(() => import("./pages/ArchetypesIndex.tsx"));
const ArchetypesSocial = lazyRoute(() => import("./pages/ArchetypesSocial.tsx"));
const ArchetypesOperator = lazyRoute(() => import("./pages/ArchetypesOperator.tsx"));
const ArchetypesWar = lazyRoute(() => import("./pages/ArchetypesWar.tsx"));
const CasualPatrol = lazyRoute(() => import("./pages/CasualPatrol.tsx"));
const StakedPatrol = lazyRoute(() => import("./pages/StakedPatrol.tsx"));
const FireteamPatrol = lazyRoute(() => import("./pages/FireteamPatrol.tsx"));
const WhyTGH = lazyRoute(() => import("./pages/WhyTGH.tsx"));
const MediaKit = lazyRoute(() => import("./pages/MediaKit.tsx"));
const RanksGlossary = lazyRoute(() => import("./pages/RanksGlossary.tsx"));
const Tonight = lazyRoute(() => import("./pages/Tonight.tsx"));
const PlayNow = lazyRoute(() => import("./pages/PlayNow.tsx"));
const Solo = lazyRoute(() => import("./pages/Solo.tsx"));
const WelcomeAboard = lazyRoute(() => import("./pages/WelcomeAboard.tsx"));
const Analytics = lazyRoute(() => import("./pages/admin/Analytics.tsx"));
const Shop = lazyRoute(() => import("./pages/Shop.tsx"));
const EsportsAbout = lazyRoute(() => import("./pages/Esports.tsx"));
const MilsimAbout = lazyRoute(() => import("./pages/Milsim.tsx"));
const EsportsApply = lazyRoute(() => import("./pages/EsportsApply.tsx"));
const OrgsHub = lazyRoute(() => import("./pages/OrgsHub.tsx"));
const PartnerOrgEditor = lazyRoute(() => import("./pages/PartnerOrgEditor.tsx"));
const PartnerOrgPage = lazyRoute(() => import("./pages/PartnerOrgPage.tsx"));
const AlliedCommunities = lazyRoute(() => import("./pages/AlliedCommunities.tsx"));
const SovTokens = lazyRoute(() => import("./pages/SovTokens.tsx"));
const WarzoneLFG = lazyRoute(() => import("./pages/WarzoneLFG.tsx"));
const AdultGamingCommunity = lazyRoute(() => import("./pages/AdultGamingCommunity.tsx"));
const FindTeammatesWithMics = lazyRoute(() => import("./pages/FindTeammatesWithMics.tsx"));
const Socom2Community = lazyRoute(() => import("./pages/Socom2Community.tsx"));
const EstSquad = lazyRoute(() => import("./pages/EstSquad.tsx"));
const NonToxicGamingCommunity = lazyRoute(() => import("./pages/NonToxicGamingCommunity.tsx"));
const ApexLFG = lazyRoute(() => import("./pages/ApexLFG.tsx"));
const ArcRaidersLFG = lazyRoute(() => import("./pages/ArcRaidersLFG.tsx"));
const Helldivers2LFG = lazyRoute(() => import("./pages/Helldivers2LFG.tsx"));
const Battlefield6LFG = lazyRoute(() => import("./pages/Battlefield6LFG.tsx"));
const R6SiegeLFG = lazyRoute(() => import("./pages/R6SiegeLFG.tsx"));
const HellLetLooseLFG = lazyRoute(() => import("./pages/HellLetLooseLFG.tsx"));
const InsurgencyLFG = lazyRoute(() => import("./pages/InsurgencyLFG.tsx"));
const FortniteLFG = lazyRoute(() => import("./pages/FortniteLFG.tsx"));
const ClanRecruitment = lazyRoute(() => import("./pages/ClanRecruitment.tsx"));
const CommsRequiredDiscord = lazyRoute(() => import("./pages/CommsRequiredDiscord.tsx"));
const MatureWarzoneClan = lazyRoute(() => import("./pages/MatureWarzoneClan.tsx"));
const JoinTheNetwork = lazyRoute(() => import("./pages/JoinTheNetwork.tsx"));
const CreatorsApply = lazyRoute(() => import("./pages/CreatorsApply.tsx"));
const DevsPitch = lazyRoute(() => import("./pages/DevsPitch.tsx"));
const CreatorsDirectory = lazyRoute(() => import("./pages/CreatorsDirectory.tsx"));
const CreatorProfile = lazyRoute(() => import("./pages/CreatorProfile.tsx"));

const queryClient = new QueryClient();

const AnimatedRoutes = () => {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/homepage" element={<Navigate to="/" replace />} />
        <Route path="/about" element={<About />} />
        <Route path="/memberships" element={<Memberships />} />
        <Route path="/access" element={<Access />} />
        <Route path="/access/coaching" element={<AccessCoaching />} />
        <Route path="/access/support" element={<AccessSupport />} />
        <Route path="/access/donate" element={<Navigate to="/access/support" replace />} />
        <Route path="/access/grants" element={<Navigate to="/access/support" replace />} />
        <Route path="/squadup" element={<SquadUp />} />
        <Route path="/squad-up" element={<Navigate to="/squadup" replace />} />
        {/* C1 — /start is the logged-out hub bundling Squad Up, Operator Profile, Live Ops, Ranks. */}
        <Route path="/start" element={<StartHub />} />
        <Route path="/get-started" element={<Navigate to="/start" replace />} />
        <Route path="/featured" element={<Featured />} />
        <Route path="/mission" element={<Mission />} />
        <Route path="/tacticalloadouts" element={<TacticalLoadouts />} />
        <Route path="/courses" element={<TacticalLoadouts />} />
        <Route path="/free-training" element={<FreeTraining />} />
        <Route path="/discord-help" element={<DiscordHelp />} />
        <Route path="/discord-setup" element={<DiscordSetup />} />
        <Route path="/discord-embed" element={<DiscordEmbed />} />
        <Route path="/join-discord" element={<JoinDiscord />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/games" element={<Games />} />
        <Route path="/games/:game" element={<GamePage />} />
        <Route path="/ranks" element={<RanksAccolades />} />
        <Route path="/leaderboard" element={<Accolades />} />
        <Route path="/clans" element={<ClanDirectory />} />
        <Route path="/find-a-unit" element={<FindAUnit />} />
        <Route path="/clans/:tag" element={<ClanPage />} />
        <Route path="/rivalry/:matchup" element={<RivalryPage />} />
        <Route path="/members/:username" element={<MemberProfilePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/profile" element={<MyProfile />} />
        <Route path="/my-teams" element={<MySquad />} />
        <Route path="/submit" element={<OfficerTools />} />
        <Route path="/academy" element={<OfficerTools />} />
        <Route path="/officer-tools" element={<OfficerTools />} />
        <Route path="/events" element={<Events />} />
        <Route path="/lfg" element={<SquadFinder />} />
        <Route path="/recap" element={<WeeklyRecap />} />
        <Route path="/playbook" element={<Playbook />} />
        <Route path="/command-groups" element={<CommandGroups />} />
        <Route path="/prizes" element={<Prizes />} />
        <Route path="/about/summary" element={<Summary />} />
        <Route path="/operator-profile" element={<OperatorProfile />} />
        <Route path="/share/operator/:userId" element={<ShareOperatorProfile />} />
        <Route path="/share/member/:username" element={<ShareMemberProfile />} />
        <Route path="/help" element={<Help />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/clan-wars" element={<ClanWars />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/find-teammates" element={<FindTeammates />} />
        {/* High-intent SEO landing pages — own specific keyword clusters */}
        <Route path="/warzone-lfg" element={<WarzoneLFG />} />
        <Route path="/adult-gaming-community" element={<AdultGamingCommunity />} />
        <Route path="/mature-gaming-discord" element={<Navigate to="/adult-gaming-community" replace />} />
        <Route path="/find-teammates-with-mics" element={<FindTeammatesWithMics />} />
        <Route path="/mic-required-discord" element={<Navigate to="/find-teammates-with-mics" replace />} />
        <Route path="/socom-2-community" element={<Socom2Community />} />
        <Route path="/socom-ii-community" element={<Navigate to="/socom-2-community" replace />} />
        <Route path="/est-squad" element={<EstSquad />} />
        <Route path="/weeknight-squad" element={<Navigate to="/est-squad" replace />} />
        <Route path="/late-night-squad" element={<Navigate to="/est-squad" replace />} />
        <Route path="/non-toxic-gaming-community" element={<NonToxicGamingCommunity />} />
        <Route path="/friendly-gaming-community" element={<Navigate to="/non-toxic-gaming-community" replace />} />
        {/* Game-specific LFG landing pages */}
        <Route path="/apex-lfg" element={<ApexLFG />} />
        <Route path="/apex-legends-lfg" element={<Navigate to="/apex-lfg" replace />} />
        <Route path="/find-apex-teammates" element={<Navigate to="/apex-lfg" replace />} />
        <Route path="/arc-raiders-lfg" element={<ArcRaidersLFG />} />
        <Route path="/find-arc-raiders-teammates" element={<Navigate to="/arc-raiders-lfg" replace />} />
        <Route path="/helldivers-2-lfg" element={<Helldivers2LFG />} />
        <Route path="/helldivers-lfg" element={<Navigate to="/helldivers-2-lfg" replace />} />
        <Route path="/battlefield-6-lfg" element={<Battlefield6LFG />} />
        <Route path="/bf6-lfg" element={<Navigate to="/battlefield-6-lfg" replace />} />
        <Route path="/r6-siege-lfg" element={<R6SiegeLFG />} />
        <Route path="/r6-lfg" element={<Navigate to="/r6-siege-lfg" replace />} />
        <Route path="/rainbow-six-lfg" element={<Navigate to="/r6-siege-lfg" replace />} />
        <Route path="/hell-let-loose-lfg" element={<HellLetLooseLFG />} />
        <Route path="/hll-lfg" element={<Navigate to="/hell-let-loose-lfg" replace />} />
        <Route path="/insurgency-lfg" element={<InsurgencyLFG />} />
        <Route path="/insurgency-sandstorm-lfg" element={<Navigate to="/insurgency-lfg" replace />} />
        <Route path="/sandstorm-lfg" element={<Navigate to="/insurgency-lfg" replace />} />
        <Route path="/fortnite-lfg" element={<FortniteLFG />} />
        <Route path="/adult-fortnite-squad" element={<Navigate to="/fortnite-lfg" replace />} />
        {/* Cross-cutting SEO landing pages */}
        <Route path="/clan-recruitment" element={<ClanRecruitment />} />
        <Route path="/gaming-clan-recruitment" element={<Navigate to="/clan-recruitment" replace />} />
        <Route path="/casual-team-recruitment" element={<Navigate to="/clan-recruitment" replace />} />
        <Route path="/comms-required-discord" element={<CommsRequiredDiscord />} />
        <Route path="/coordinated-squads" element={<Navigate to="/comms-required-discord" replace />} />
        <Route path="/structured-squads" element={<Navigate to="/comms-required-discord" replace />} />
        <Route path="/mature-warzone-clan" element={<MatureWarzoneClan />} />
        <Route path="/adult-warzone-clan" element={<Navigate to="/mature-warzone-clan" replace />} />
        {/* Tier-2 game LFG aliases (redirect to existing game hubs) */}
        <Route path="/cs2-lfg" element={<Navigate to="/games/cs2" replace />} />
        <Route path="/marvel-rivals-lfg" element={<Navigate to="/games/marvel-rivals" replace />} />
        <Route path="/delta-force-lfg" element={<Navigate to="/games/delta-force" replace />} />
        <Route path="/arma-reforger-lfg" element={<Navigate to="/games/arma-reforger" replace />} />
        <Route path="/squad-game-lfg" element={<Navigate to="/games/squad-game" replace />} />
        <Route path="/gray-zone-warfare-lfg" element={<Navigate to="/games/gray-zone-warfare" replace />} />
        <Route path="/ready-or-not-lfg" element={<Navigate to="/games/ready-or-not" replace />} />
        <Route path="/pubg-lfg" element={<Navigate to="/games/pubg" replace />} />
        {/* SEO aliases — capture high-intent search phrases without duplicating content */}
        <Route path="/find-gaming-teammates" element={<Navigate to="/find-teammates" replace />} />
        <Route path="/tactical-gaming-discord" element={<Navigate to="/join-discord" replace />} />
        <Route path="/gaming-discord" element={<Navigate to="/join-discord" replace />} />
        <Route path="/warzone-squad-finder" element={<Navigate to="/games/warzone" replace />} />
        <Route path="/warzone-teammates" element={<Navigate to="/games/warzone" replace />} />
        <Route path="/arc-raiders-team-finder" element={<Navigate to="/games/arc-raiders" replace />} />
        <Route path="/arc-raiders-teammates" element={<Navigate to="/games/arc-raiders" replace />} />
        <Route path="/rainbow-six-siege-squad-finder" element={<Navigate to="/games/r6-siege" replace />} />
        <Route path="/r6-siege-teammates" element={<Navigate to="/games/r6-siege" replace />} />
        <Route path="/socom-2-squad-finder" element={<Navigate to="/games/socom-2" replace />} />
        <Route path="/battlefield-6-squad-finder" element={<Navigate to="/games/battlefield-6" replace />} />
        <Route path="/delta-force-squad-finder" element={<Navigate to="/games/delta-force" replace />} />
        <Route path="/chill-gaming-squads" element={<Navigate to="/chill-squads" replace />} />
        <Route path="/tactical-squads" element={<Navigate to="/war-track" replace />} />
        <Route path="/chill-squads" element={<SocialOps />} />
        <Route path="/social-ops" element={<Navigate to="/chill-squads" replace />} />
        <Route path="/operator-path" element={<OperatorPath />} />
        <Route path="/war-track" element={<WarTrack />} />
        <Route path="/wars/lobby/:id" element={<WarLobby />} />
        <Route path="/spotlight" element={<Spotlight />} />
        <Route path="/spotlight/wyldejezter" element={<SpotlightWyldeJezter />} />
        <Route path="/bring-your-crew" element={<BringYourCrew />} />
        <Route path="/archetypes" element={<ArchetypesIndex />} />
        <Route path="/archetypes/social" element={<ArchetypesSocial />} />
        <Route path="/archetypes/operator" element={<ArchetypesOperator />} />
        <Route path="/archetypes/war" element={<ArchetypesWar />} />
        <Route path="/casual-patrol" element={<CasualPatrol />} />
        <Route path="/staked-patrol" element={<StakedPatrol />} />
        <Route path="/fireteam-patrol" element={<FireteamPatrol />} />
        <Route path="/why-tgh" element={<WhyTGH />} />
        <Route path="/media" element={<MediaKit />} />
        <Route path="/glossary" element={<RanksGlossary />} />
        <Route path="/tonight" element={<Tonight />} />
        {/* C3 — Discord deep-link router. Use ?lane=chill|operator|war */}
        <Route path="/play-now" element={<PlayNow />} />
        <Route path="/solo" element={<Solo />} />
        <Route path="/train" element={<Navigate to="/solo" replace />} />
        <Route path="/welcome-aboard" element={<WelcomeAboard />} />
        <Route path="/esports" element={<OrgsHub kind="esports" />} />
        <Route path="/esports/about" element={<EsportsAbout />} />
        <Route path="/esports/apply" element={<EsportsApply />} />
        <Route path="/esports/new" element={<PartnerOrgEditor kind="esports" mode="new" />} />
        <Route path="/esports/edit/:id" element={<PartnerOrgEditor kind="esports" mode="edit" />} />
        <Route path="/esports/:slug" element={<PartnerOrgPage kind="esports" />} />
        <Route path="/esports-orgs" element={<Navigate to="/esports" replace />} />
        <Route path="/milsim" element={<OrgsHub kind="milsim" />} />
        <Route path="/allied-communities" element={<AlliedCommunities />} />
        <Route path="/orgs" element={<Navigate to="/allied-communities" replace />} />
        <Route path="/milsim/about" element={<MilsimAbout />} />
        <Route path="/milsim/new" element={<PartnerOrgEditor kind="milsim" mode="new" />} />
        <Route path="/milsim/edit/:id" element={<PartnerOrgEditor kind="milsim" mode="edit" />} />
        <Route path="/milsim/:slug" element={<PartnerOrgPage kind="milsim" />} />
        <Route path="/milsim-units" element={<Navigate to="/milsim" replace />} />
        <Route path="/network" element={<JoinTheNetwork />} />
        <Route path="/join-the-network" element={<Navigate to="/network" replace />} />
        <Route path="/get-on-tgh" element={<Navigate to="/network" replace />} />
        <Route path="/list-your-org" element={<Navigate to="/network" replace />} />
        <Route path="/creators" element={<CreatorsDirectory />} />
        <Route path="/creators/apply" element={<CreatorsApply />} />
        <Route path="/devs" element={<DevsPitch />} />
        <Route path="/pitch-a-game" element={<Navigate to="/devs" replace />} />
        <Route path="/creators/:slug" element={<CreatorProfile />} />
        <Route path="/streamers" element={<Navigate to="/creators" replace />} />
        <Route path="/streamers/apply" element={<Navigate to="/creators/apply" replace />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/apparel" element={<Shop />} />
        <Route path="/shop/gear" element={<Shop />} />
        <Route path="/sov-tokens" element={<SovTokens />} />
        <Route path="/admin/analytics" element={<Analytics />} />
        <Route path="/admin/play-now-analytics" element={<Navigate to="/admin/analytics" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const RoutesWithBoundary = () => {
  const location = useLocation();

  return (
    <RouteErrorBoundary resetKey={location.pathname}>
      <AnimatedRoutes />
    </RouteErrorBoundary>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Sonner />
        <BrowserRouter>
          <RouteScrollToTop />
          <RoutesWithBoundary />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
